import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { UserRole } from '../../../types/user';
import { Service } from '../../../types/service';
import { UserSelectionData } from '../../../services/userService';
import { getServicesByTargetRole } from '../../../services/serviceService';
import { getUsersByRoleForSelection } from '../../../services/userService';
import { createServiceRequest } from '../../../services/serviceRequestService';
import { uploadMultipleFiles } from '../../../services/uploadService';
import { CreateAdminServiceRequestData } from '../../../services/serviceRequestService';
import formStyles from '../../../components/forms/Form.module.css';
import { FaUpload } from 'react-icons/fa';
import Icon from '../../../components/common/Icon';
import pageStyles from '../../Page.module.css';

type FormInputs = {
  targetRole: UserRole | '';
  userId: string;
  serviceId: string;
  noteText: string;
  documents: FileList;
};

const AdminCreateRequestPage: React.FC = () => {
  const [targetRole, setTargetRole] = useState<UserRole | ''>('');
  const [users, setUsers] = useState<UserSelectionData[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isLoading, setIsLoading] = useState({ users: false, services: false });
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>({ defaultValues: { targetRole: '' } });

  const selectedRole = watch('targetRole');
  const selectedServiceId = watch('serviceId');

  // جلب المستخدمين والخدمات عند تغيير الدور المستهدف
  useEffect(() => {
    if (!selectedRole) {
      setUsers([]);
      setServices([]);
      return;
    }

    const fetchData = async () => {
      setIsLoading({ users: true, services: true });
      try {
        const usersPromise = getUsersByRoleForSelection(selectedRole);
        const servicesPromise = getServicesByTargetRole(selectedRole);
        const [usersRes, servicesRes] = await Promise.all([usersPromise, servicesPromise]);
        setUsers(usersRes);
        setServices(servicesRes);
      } catch (error) {
        toast.error('فشل في جلب البيانات للدور المحدد.');
      } finally {
        setIsLoading({ users: false, services: false });
      }
    };

    fetchData();
    setValue('userId', ''); // إعادة تعيين المستخدم والخدمة عند تغيير الدور
    setValue('serviceId', '');
  }, [selectedRole, setValue]);

  // تحديث الخدمة المختارة وسعرها
  useEffect(() => {
    const service = services.find(s => s.id === parseInt(selectedServiceId));
    setSelectedService(service || null);
  }, [selectedServiceId, services]);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
  // --- التحقق من الخدمة (يبقى كما هو) ---
  if (!selectedService) {
    toast.error('يرجى اختيار خدمة صالحة.');
    return;
  }

  // --- رفع الملفات (يبقى كما هو) ---
  let uploadedDocuments: { name: string; url: string }[] = [];
  if (data.documents && data.documents.length > 0) {
    setIsUploading(true);
    try {
      const uploadedFiles = await uploadMultipleFiles(data.documents);
      uploadedDocuments = uploadedFiles.map(file => ({ name: file.originalname, url: file.url }));
    } catch (error) {
      toast.error('فشل في رفع المستندات.');
      setIsUploading(false);
      return;
    } finally {
      setIsUploading(false);
    }
  }

  // --- تجميع بيانات الطلب (يبقى كما هو) ---
  const requestData: CreateAdminServiceRequestData = {
    user_id: parseInt(data.userId),
    service_id: selectedService.id,
    fee: selectedService.fee,
    note_text: data.noteText || undefined,
    documents: uploadedDocuments.length > 0 ? uploadedDocuments : undefined,
  };

  // ========================================================================
  // (1) !! الإصلاح الرئيسي هنا: استدعاء واحد فقط !!
  // ========================================================================
  
  // استدعِ الدالة مرة واحدة فقط وخزن الوعد الذي تعيده
  const promise = createServiceRequest(requestData);

  // مرر هذا الوعد مباشرة إلى toast.promise
  toast.promise(promise, {
    loading: 'جاري إنشاء الطلب...',
    success: (result) => {
      // عندما ينجح الوعد، سيتم تنفيذ هذا الكود
      console.log("✅ Promise resolved successfully with result:", result);
      navigate('/admin/service-requests/list');
      return 'تم إنشاء الطلب بنجاح!';
    },
    error: (err: any) => {
      // عندما يفشل الوعد، سيتم تنفيذ هذا الكود
      console.error("❌ Promise rejected with error:", err);
      return err.response?.data?.message || 'فشل في إنشاء الطلب.';
    },
  });
};


  const totalLoading = isSubmitting || isUploading || isLoading.users || isLoading.services;

  return (
    <div className={formStyles.pageContainer}>
      <div className={formStyles.formContainer}>
        <h2 className={formStyles.title}>إنشاء طلب خدمة جديد (بواسطة المشرف)</h2>
        <form onSubmit={handleSubmit(onSubmit)} className={formStyles.form}>
          {/* 1. اختيار تصنيف الخدمة (الدور) */}
          <div className={formStyles.formField}>
            <label htmlFor="targetRole" className={formStyles.label}>تصنيف الخدمة</label>
            <select id="targetRole" {...register('targetRole', { required: 'يجب اختيار تصنيف' })} className={formStyles.input}>
              <option value="">-- اختر التصنيف --</option>
              <option value={UserRole.SUBSCRIBER}>مشترك جديد</option>
              <option value={UserRole.TRAINEE}>متدرب</option>
              <option value={UserRole.LAWYER}>محامي</option>
            </select>
            {errors.targetRole && <p className={formStyles.error}>{errors.targetRole.message}</p>}
          </div>

          {selectedRole && (
            <>
              {/* 2. اختيار المستخدم */}
              <div className={formStyles.formField}>
                <label htmlFor="userId" className={formStyles.label}>مقدم الطلب</label>
                <select id="userId" {...register('userId', { required: 'يجب اختيار مقدم الطلب' })} className={formStyles.input} disabled={isLoading.users}>
                  <option value="">{isLoading.users ? 'جاري التحميل...' : '-- اختر المستخدم --'}</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.displayText}</option>
                  ))}
                </select>
                {errors.userId && <p className={formStyles.error}>{errors.userId.message}</p>}
              </div>

              {/* 3. اختيار الخدمة */}
              <div className={formStyles.formField}>
                <label htmlFor="serviceId" className={formStyles.label}>الخدمة المطلوبة</label>
                <select id="serviceId" {...register('serviceId', { required: 'يجب اختيار الخدمة' })} className={formStyles.input} disabled={isLoading.services}>
                  <option value="">{isLoading.services ? 'جاري التحميل...' : '-- اختر الخدمة --'}</option>
                  {services.map(service => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
                {errors.serviceId && <p className={formStyles.error}>{errors.serviceId.message}</p>}
              </div>

              {/* عرض الرسوم */}
              {selectedService && (
                <div className={formStyles.feeDisplay}>
                  <strong>رسوم الخدمة:</strong>
                  <span>{selectedService.fee} ريال</span>
                </div>
              )}

              {/* 4. الملاحظات ورفع الملفات */}
              <div className={formStyles.formField}>
                <label htmlFor="noteText" className={formStyles.label}>ملاحظات (اختياري)</label>
                <textarea id="noteText" {...register('noteText')} className={formStyles.textarea} rows={3} />
              </div>
              <div className={formStyles.formField}>
                <label htmlFor="documents" className={formStyles.label}>إرفاق مستندات (اختياري)</label>
                <div className={formStyles.fileInputContainer}>
                  <input type="file" id="documents" {...register('documents')} multiple className={formStyles.fileInput} />
                  <label htmlFor="documents" className={formStyles.fileInputLabel}><Icon as={FaUpload} style={pageStyles.icon } /><span>اختر الملفات...</span></label>
                </div>
              </div>
            </>
          )}

          <button type="submit" disabled={totalLoading || !selectedRole} className={formStyles.submitButton}>
            {totalLoading ? 'جاري التحميل...' : 'إنشاء الطلب'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminCreateRequestPage;
